import type {
  OverviewDateRange,
  OverviewGroup,
  OverviewResponseTotals,
  OverviewSeriesMap,
} from '../models/overview.types'

import { aggregateMultiMetricSeries } from '@/features/reports'

interface AggregateOverviewInput {
  rows: readonly Record<string, unknown>[]
  metrics: readonly string[]
  group: OverviewGroup
  dateKeyOf: (row: Readonly<Record<string, unknown>>) => string
  dateRange?: OverviewDateRange | null
  responseTotals?: OverviewResponseTotals | null
}

function metricNameOf(metric: string, group: OverviewGroup): string {
  let result = metric
  if (group !== 'overall') {
    result = result.replace('purchases', 'purchase').replace('purchase_value', 'purchase_revenue')
  }
  return result.endsWith('_count') ? result.slice(0, -'_count'.length) : result
}

function periodOf(
  row: Readonly<Record<string, unknown>>,
  dateKey: string,
  dateRange: OverviewDateRange | null | undefined,
): 'current' | 'before' {
  if (!dateRange) return row.is_previous ? 'before' : 'current'
  // Compare on the frequency-aware key, not a hardcoded `row.date`: weekly/monthly/yearly rows
  // carry `week`/`month_of_year`/`year` instead of `date`, and `'' >= start` classified every
  // non-daily row as 'before', leaving the 'current' series empty (blank chart on frequency
  // change — ORP-18967 defect 2, live-reproduced against staging).
  return dateKey >= dateRange.start && dateKey <= dateRange.end ? 'current' : 'before'
}

/** Summary-specific aliases/date-window semantics over module 08's aggregation authority. */
export function aggregateOverviewSeries(input: AggregateOverviewInput): OverviewSeriesMap | null {
  return aggregateMultiMetricSeries({
    rows: input.rows,
    metrics: input.metrics,
    dateKeyOf: input.dateKeyOf,
    platformOf: (row) => (row.platform ? String(row.platform).toLowerCase() : 'all'),
    metricFieldOf: (metric) => metricNameOf(metric, input.group),
    periodOf: (row, dateKey) => periodOf(row, dateKey, input.dateRange),
    ...(input.responseTotals !== undefined ? { responseTotals: input.responseTotals } : {}),
  })
}
